import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import dayjs from 'dayjs';
import { FiChevronLeft, FiChevronRight, FiCalendar, FiMapPin } from 'react-icons/fi';
import * as slotSettingActions from '../../Redux/Actions/slotSettings';
import DateOverrideFormModal from './dateOverrideFormModal';
import { SectionCard, PageLoader, ErrorState, EmptyState } from './common';
import { CenterSelect, MonthField } from './fields';
import { DAYS, DATE_OVERRIDE_TYPE, toCenterId, getErrorMessage, formatDate, formatTimeRange, formatClosedRange } from './helpers';

const LEGEND = [
    { kind: 'open', pill: 'is-open', label: 'Open' },
    { kind: 'off', pill: 'is-off', label: 'Weekly off' },
    { kind: 'holiday', pill: 'is-holiday', label: 'Holiday' },
    { kind: 'disabled', pill: 'is-muted', label: 'Not bookable' }
];

const dayKind = (day) => {
    if (!day.isClosed) return 'open';
    if (day.reason === 'Weekly off') return 'off';
    if (day.reason && day.reason.startsWith('Holiday')) return 'holiday';
    return 'disabled';
};

const dayText = (day, kind) => {
    if (kind === 'open') return day.openFrom || day.openTo ? formatTimeRange(day.openFrom, day.openTo) : 'All day';
    if (kind === 'off') return 'Off';
    if (kind === 'holiday') return day.reason.replace(/^Holiday:\s*/, '');
    return day.reason || 'Closed';
};

const CalendarTab = ({ centers, centersLoading, centersError, onRetryCenters, refreshKey, onSaved }) => {
    const [centerId, setCenterId] = useState('');
    const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [addDay, setAddDay] = useState(null);
    const requestId = useRef(0);
    const today = dayjs().format('YYYY-MM-DD');

    useEffect(() => {
        if (centerId === '' && centers.length) setCenterId(String(centers[0].value));
    }, [centers, centerId]);

    const loadCalendar = useCallback(() => {
        if (centerId === '' || !month) return;
        const current = ++requestId.current;
        setLoading(true);
        setLoadError('');
        slotSettingActions.getCalendar(toCenterId(centerId), month).then(res => {
            if (current !== requestId.current) return;
            setData(res);
            setLoading(false);
        }).catch(err => {
            if (current !== requestId.current) return;
            setData(null);
            setLoading(false);
            setLoadError(getErrorMessage(err, 'Unable to load calendar'));
        });
    }, [centerId, month]);

    useEffect(() => {
        loadCalendar();
    }, [loadCalendar, refreshKey]);

    const shiftMonth = (step) => setMonth(dayjs(`${month}-01`).add(step, 'month').format('YYYY-MM'));

    const days = data?.days || [];
    const leadingBlanks = days.length ? Math.max(DAYS.indexOf(days[0].dayOfWeek), 0) : 0;
    const cells = [...Array(leadingBlanks).fill(null), ...days];
    while (cells.length % 7 !== 0) cells.push(null);

    const counts = days.reduce((acc, day) => {
        const kind = dayKind(day);
        acc[kind] = (acc[kind] || 0) + 1;
        return acc;
    }, {});

    const handleSaved = (res) => {
        setAddDay(null);
        onSaved(res);
    };

    const renderCalendar = () => {
        if (centersLoading) return <PageLoader text="Loading centers..." />;
        if (centersError) return <ErrorState title="Could not load centers" message={centersError} onRetry={onRetryCenters} />;
        if (!centers.length) return <EmptyState icon={<FiMapPin />} title="No active centers" text="There are no active centers to show." />;
        if (loading) return <PageLoader text="Loading calendar..." />;
        if (loadError) return <ErrorState message={loadError} onRetry={loadCalendar} />;
        if (!days.length) return <EmptyState icon={<FiCalendar />} title="No days returned" text="The calendar has no data for this month." />;

        return (
            <>
                <div className="ss-summary mb-3">
                    {LEGEND.map(item => (
                        <span key={item.kind} className={`ss-pill ${item.pill}`}>{counts[item.kind] || 0} {item.label.toLowerCase()}</span>
                    ))}
                </div>
                <div className="ss-cal-grid">
                    {DAYS.map(day => <div key={day} className="ss-cal-weekday">{day.substring(0, 3)}</div>)}
                    {cells.map((day, index) => {
                        if (!day) return <div key={`blank-${index}`} className="ss-cal-blank" />;
                        const kind = dayKind(day);
                        const text = dayText(day, kind);
                        const isToday = day.date === today;
                        const clickable = day.date >= today;
                        const action = day.isClosed ? 'mark it as a special opening' : 'mark it as a holiday';
                        return (
                            <button
                                key={day.date}
                                type="button"
                                className={`ss-cal-day is-${kind} ${isToday ? 'is-today' : ''}`}
                                title={clickable ? `${formatDate(day.date)}: ${text}. Click to ${action}.` : `${formatDate(day.date)}: ${text}`}
                                disabled={!clickable}
                                aria-disabled={!clickable}
                                onClick={() => clickable && setAddDay(day)}
                            >
                                <span className="ss-cal-date">
                                    {Number(day.date.substring(8, 10))}
                                    {isToday && <span className="ss-cal-today">Today</span>}
                                </span>
                                <span className="ss-cal-status">{text}</span>
                                {kind === 'open' && (day.closedRanges || []).map(range => (
                                    <span key={range} className="ss-cal-closed">Closed {formatClosedRange(range)}</span>
                                ))}
                            </button>
                        );
                    })}
                </div>
                <p className="ss-hint mt-3 mb-0">Click a date to mark it: an open day becomes a holiday, a closed day (weekly off, holiday, outside the booking window) a special opening. Past dates can't be selected.</p>
            </>
        );
    };

    return (
        <>
            <SectionCard>
                <div className="ss-cal-toolbar">
                    <div className="ss-cal-center">
                        <Form.Label htmlFor="cal-center">Center</Form.Label>
                        <CenterSelect id="cal-center" value={centerId} centers={centers} loading={centersLoading}
                            disabled={centersLoading || !centers.length} onChange={setCenterId} />
                    </div>
                    <div>
                        <Form.Label htmlFor="cal-month">Month</Form.Label>
                        <div className="ss-month-nav">
                            <button type="button" className="ss-nav-btn" aria-label="Previous month" onClick={() => shiftMonth(-1)}><FiChevronLeft /></button>
                            <div className="ss-month-picker">
                                <MonthField id="cal-month" value={month} onChange={setMonth} />
                            </div>
                            <button type="button" className="ss-nav-btn" aria-label="Next month" onClick={() => shiftMonth(1)}><FiChevronRight /></button>
                            <Button variant="light" disabled={month === dayjs().format('YYYY-MM')}
                                onClick={() => setMonth(dayjs().format('YYYY-MM'))}>This month</Button>
                        </div>
                    </div>
                </div>
            </SectionCard>

            <SectionCard
                title={data?.centerName ? `${data.centerName} · ${dayjs(`${month}-01`).format('MMMM YYYY')}` : 'Calendar'}
                subtitle="Open hours, weekly off days and holidays for the selected center."
                actions={(
                    <div className="ss-legend">
                        {LEGEND.map(item => <span key={item.kind} className={`ss-pill ${item.pill}`}>{item.label}</span>)}
                    </div>
                )}
            >
                {renderCalendar()}
            </SectionCard>

            <DateOverrideFormModal
                show={!!addDay}
                item={null}
                defaults={addDay ? {
                    type: addDay.isClosed ? DATE_OVERRIDE_TYPE.OPEN : DATE_OVERRIDE_TYPE.CLOSED,
                    centerId,
                    fromDate: addDay.date,
                    toDate: addDay.date
                } : null}
                centers={centers}
                onClose={() => setAddDay(null)}
                onSaved={handleSaved}
            />
        </>
    );
};

export default CalendarTab;
